import React, { Children, ReactNode, useEffect, useRef, useState } from 'react';
import { TinySliderInstance as OriginalTinySliderInstance, TinySliderSettings, tns } from 'tiny-slider';

import 'tiny-slider/dist/tiny-slider.css';

type TinySliderInstance = OriginalTinySliderInstance & {
	_resume?: () => void;
};

export type TinySliderWrapperProps = TinySliderSettings & {
	children: ReactNode;
	dataTestId?: string;
};

// Inspired by the react-tiny-slider library:
// https://github.com/Maclay74/react-tiny-slider
const TinySliderWrapper = ({ children, dataTestId = 'tiny-slider-wrapper', ...options }: TinySliderWrapperProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const sliderInstanceRef = useRef<TinySliderInstance | null>(null);
	const mountedRef = useRef(false);
	const [mounted, setMounted] = useState(false);
	const slidesCount = Children.count(children);

	useEffect(() => {
		mountedRef.current = true;
		setMounted(true);
		return () => {
			mountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		if (!mounted) return;
		if (!containerRef.current) return;
		if (!slidesCount) return;
		if (sliderInstanceRef.current) return;

		const container = containerRef.current;

		sliderInstanceRef.current = tns({
			container,
			...options
		});

		if (options.autoplay && options.nav) {
			const info = sliderInstanceRef.current.getInfo();
			const navContainer = info.navContainer;
			const resume = () => {
				if (sliderInstanceRef.current?.play) sliderInstanceRef.current.play();
			};
			navContainer?.addEventListener('click', resume);
			sliderInstanceRef.current._resume = resume;
		}
	}, [mounted, slidesCount, options]);

	useEffect(() => {
		return () => {
			const inst = sliderInstanceRef.current;
			if (inst) {
				if (inst._resume) {
					const info = inst.getInfo();
					info.navContainer?.removeEventListener('click', inst._resume);
				}
				inst.destroy();
			}
			sliderInstanceRef.current = null;
		};
	}, []);

	return (
		<div ref={containerRef} data-testid={dataTestId}>
			{children}
		</div>
	);
};

export default TinySliderWrapper;
